import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, cn } from '@bliks-io/ui';

import { useSetTenantAppSeatLimit } from '@/pages/admin/api/queries/useTenantApps';
import type { MergedAppRow } from '@/lib/merge-app-catalog';
import { formatSeatLimit, type ParsedSeatLimit } from '@/lib/seat-limits';
import { SeatLimitInput } from './SeatLimitInput';

interface TenantAppSeatLimitCellProps {
  tenantId: string;
  app: MergedAppRow;
}

/**
 * Seats assigned against the cap, with the cap editable in place. Committing on
 * blur/Enter saves without a second control; an unparseable entry is refused
 * instead of being read as "clear the cap".
 */
export function TenantAppSeatLimitCell({ tenantId, app }: TenantAppSeatLimitCellProps) {
  const { t } = useTranslation();
  const setSeatLimit = useSetTenantAppSeatLimit(tenantId);
  const [parsed, setParsed] = useState<ParsedSeatLimit | undefined>(undefined);

  const inputId = `app-seat-limit-${app.appId}`;
  const stored = app.seatLimit ?? null;
  const assignedCount = app.assignedUserIds?.length ?? 0;
  const overCap = stored !== null && assignedCount > stored;

  const commit = () => {
    if (!parsed?.valid || parsed.limit === stored) return;
    setSeatLimit.mutate({ appId: app.appId, seatLimit: parsed.limit });
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>{t('adminSettings.tenant.appSeatLimit')}</Label>
      <div className="flex items-center gap-3">
        <SeatLimitInput
          id={inputId}
          stored={app.seatLimit}
          onParsed={setParsed}
          onCommit={commit}
          className="w-20"
          disabled={setSeatLimit.isPending}
        />
        <span
          className={cn(
            'text-sm tabular-nums',
            overCap ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {t('common.Apps.seatsCount', { used: assignedCount, limit: formatSeatLimit(stored) })}
        </span>
      </div>
    </div>
  );
}
